import { useMemo } from 'react';
import { useLocation } from '@remix-run/react';
import type { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import type { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';
import { availableSortOptions } from '@modules-rocco/search/config/sort-options.config';
import { parseURL } from '@modules-rocco/url/api/parse-url';

interface SortOption {
    title: string;
    value: SearchSortOptions;
    selected: boolean;
    default: boolean;
}

/**
 * Custom hook to generate sorting options based on the provided category and user view.
 *
 * @param {SearchIndexes|null} searchIndex - The index from Search Service, or null if no searchIndex is selected.
 * @param {boolean} hasMarketingView - Boolean indicating if the user has marketing view permissions.
 * @returns {SortOption[]} - An object containing the sort options, search parameters, and current location.
 */
export const useSorts = (
    searchIndex: SearchIndexes | null,
    hasMarketingView: boolean
): SortOption[] => {
    const location = useLocation();
    const isSearchRoute = location.pathname.startsWith('/search');

    const { params } = parseURL(location.pathname + location.search);
    const selectedSort = params.sort?.value ?? undefined;

    return useMemo(() => {
        const availableOptions = availableSortOptions.filter(
            (o) =>
                (!searchIndex ||
                    !o.supportSearchIndexes ||
                    o.supportSearchIndexes.includes(searchIndex)) &&
                (!o.searchPageOnly || (o.searchPageOnly && isSearchRoute)) &&
                (!o.nonSearchPage || (o.nonSearchPage && !isSearchRoute)) &&
                (!o.adminOnly || (o.adminOnly && hasMarketingView))
        );

        let selectedOptionIndex = availableOptions.findIndex(
            (o) => o.value === selectedSort
        );

        if (selectedOptionIndex === -1) selectedOptionIndex = 0;

        return availableOptions.map((o, index) => {
            return {
                title: o.title,
                value: o.value,
                selected: selectedOptionIndex === index,
                default: index === 0,
            };
        });
    }, [selectedSort, searchIndex, isSearchRoute, hasMarketingView]);
};
